import { createBrowserRouter, Navigate, Outlet, redirect, useLocation, useNavigate } from 'react-router-dom';
import AdminPageLayout from '@/layouts/AdminPageLayout';
import AdminStorePage from '@/pages/Admin/Store/AdminStorePage';
import AdminStorePageDialog from '@/pages/Admin/Store/AdminStorePageDialog';
import AdminRolePage from '@/pages/Admin/Role/AdminRolePage';
import AdminRolePageDialog from '@/pages/Admin/Role/AdminRolePageDialog';
import AdminAccountPage from '@/pages/Admin/Account/AdminAccountPage';
import AdminAccountPageDialog from '@/pages/Admin/Account/AdminAccountPageDialog';
// import AdminCorporationPage from '@/pages/Admin/Corporation/AdminCorporationPage';
import DashboardPage from '@/pages/Dashboard/DashboardPage';
import StoresPage from '@/pages/Stores/StoresPage';
import StoreMgmtPageLayout from '@/layouts/StoreMgmtPageLayout';
import StoreOverviewPage from '@/pages/StoreMgmt/overview/StoreOverviewPage';
import DevicePage from '@/pages/Device/DevicePage';
import LayoutsPage from '@/pages/Layouts/LayoutsPage';
import AppLayout from '@/layouts/AppLayout';
import StoreHistoryPage from '@/pages/StoreMgmt/history/StoreHistoryPage';
import StoreProductPage from '@/pages/StoreMgmt/product/StoreProductPage';
import StoreDevicePage from '@/pages/StoreMgmt/device/StoreDevicePage';
import RegistrationPage from '@/pages/StoreMgmt/registration/RegistrationPage';
import StoreLayoutPage from '@/pages/StoreMgmt/layout/StoreLayoutPage';
import StoreSettingsPage from '@/pages/StoreMgmt/settings/StoreSettingsPage';
import StoreLayoutPageLayout from '@/layouts/StoreLayoutPageLayout';
import LayoutsPageLayout from '@/layouts/LayoutsPageLayout';
import LoginPage from '@/pages/Login/LoginPage';
import AdminClusterPage from '@/pages/Admin/Cluster/AdminClusterPage';
import CorporationSelectionPage from '@/pages/CorporationSelectionPage/CorporationSelectionPage';
import ProtectedUserRoute from '@/layouts/ProtectedUserRoute';
import ProtectedCorpRoute from '@/layouts/ProtectedCorpRoute';
import ProtectedAdminRoute from '@/layouts/ProtectedAdminRoute';
import { withPermissionHOC } from '@/utils/permissionUtils';
import MobilePage from '@/mobile/MobilePage';
import CreditsPage from '@/pages/Credits/CreditsPage';
import ChangelogPage from '@/pages/Changelog/ChangelogPage';
import SettingsPage from '@/pages/StoreMgmt/settings/StoreSettingsPage';
import { RegistrationProvider } from '@/provider/registration/RegistrationProvider';
import ResetPasswordPage from '@/pages/ResetPassword/ResetPasswordPage';
import localforage from 'localforage';
import LayoutPackagePage from '@/pages/Layouts/LayoutPackagePage';
import AdminSettingPage from '@/pages/Admin/Settings/AdminSettingPage';
import { LayoutProvider } from '@/provider/layouts/LayoutProvider';
import { useEffect } from 'react';
import { useAppContext } from '@/provider/contexts';
import MobileSelectionPage from '@/mobile/MobileSelectionPage';
import ProtectMobileRoute from '@/mobile/ProtectMobileRoute';
import { RegistrationLifecycleProvider } from '@/provider/registration/registrationLifecycleProvider';
import { getDefaultRedirectPath, isMobileSystem } from '@/utils/deviceUtils';

const ProtectedDashboardPage = withPermissionHOC('page-dashboard-all-read')(DashboardPage);
const ProtectedStorePage = withPermissionHOC('page-stores-all-read')(StoresPage);
const ProtectedDevicesPage = withPermissionHOC('page-devices-all-read')(DevicePage);
const ProtectedStoreOverviewPage = withPermissionHOC('page-storeOverview-all-read')(StoreOverviewPage);
const ProtectedStoreProductPage = withPermissionHOC('page-storeProduct-all-read')(StoreProductPage);
const ProtectedStoreDevicePage = withPermissionHOC('page-storeDevice-all-read')(StoreDevicePage);
const ProtectedStoreRegistrationPage = withPermissionHOC('page-storeRegistration-all-read')(RegistrationPage);
const ProtectedStoreHistoryPage = withPermissionHOC('page-storeHistory-all-read')(StoreHistoryPage);

const ProtectedStoreLayoutPageLayout = withPermissionHOC('page-storelayout-all-read')(StoreLayoutPageLayout);
const ProtectedStoreLayoutPage = withPermissionHOC('page-storelayout-all-read')(StoreLayoutPage);

const SessionGuard = () => {
    // sessionGuard for browser different tabs with login/logout/switch/change behaviors.
    const navigate = useNavigate();
    const { redirectTo, setRedirectTo } = useAppContext();

    useEffect(() => {
        if (!redirectTo) return;

        navigate(`/${redirectTo}`, { replace: true });
        setRedirectTo('');
    }, [redirectTo, setRedirectTo, navigate]);

    return null;
};

const MOBILE_ENTRY_SESSION_KEY = 'mobileEntryResolved';

const MobileEntryGuard = () => {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (sessionStorage.getItem(MOBILE_ENTRY_SESSION_KEY)) return;

        const isUserLogin = localStorage.getItem('isUserLogin');
        const isCorporationLogin = !!localStorage.getItem('corporationDetails');
        if (!isUserLogin || !isCorporationLogin) return;

        sessionStorage.setItem(MOBILE_ENTRY_SESSION_KEY, '1');

        if (isMobileSystem() && !location.pathname.startsWith('/mobile')) {
            navigate('/mobile', { replace: true });
        }
    }, [location.pathname, navigate]);

    return null;
};

const DefaultRedirect = () => <Navigate to={getDefaultRedirectPath()} replace />;

const RootLayout = () => {
    return (
        <>
            <SessionGuard />
            <MobileEntryGuard />
            <Outlet />
        </>
    );
};

const router = createBrowserRouter(
    [
        {
            element: <RootLayout />,
            children: [
                {
                    path: '/login',
                    element: <LoginPage />,
                },
                {
                    path: '/reset',
                    loader: async () => {
                        const isRecovery = await localforage.getItem('recovery_token');
                        return !isRecovery ? redirect('/') : null;
                    },
                    element: <ResetPasswordPage />,
                },
                {
                    element: <ProtectedUserRoute />,
                    children: [
                        {
                            path: '/corporation-selection',
                            element: <CorporationSelectionPage />,
                        },
                        {
                            element: <ProtectedCorpRoute />,
                            children: [
                                {
                                    path: '/mobile',
                                    element: <Outlet />,
                                    children: [
                                        { index: true, element: <ProtectMobileRoute /> },
                                        {
                                            path: 'select-store',
                                            element: <MobileSelectionPage />,
                                        },
                                        { path: ':storeId', element: <MobilePage /> },
                                    ],
                                },
                                {
                                    element: <AppLayout />,
                                    children: [
                                        {
                                            path: '/',
                                            element: <DefaultRedirect />,
                                        },

                                        {
                                            path: '/dashboard',
                                            element: <ProtectedDashboardPage />,
                                        },
                                        {
                                            path: 'settings',
                                            element: <SettingsPage />,
                                        },
                                        {
                                            path: '/stores',
                                            element: <ProtectedStorePage />,
                                        },
                                        {
                                            path: 'store_management',
                                            element: <StoreMgmtPageLayout />,
                                            children: [
                                                {
                                                    path: 'overview',
                                                    element: <ProtectedStoreOverviewPage />,
                                                },
                                                {
                                                    path: 'product',
                                                    element: <ProtectedStoreProductPage />,
                                                },
                                                {
                                                    path: 'device',
                                                    element: <ProtectedStoreDevicePage />,
                                                },
                                                {
                                                    path: 'registration',
                                                    element: (
                                                        <RegistrationProvider>
                                                            <RegistrationLifecycleProvider>
                                                                <ProtectedStoreRegistrationPage />
                                                            </RegistrationLifecycleProvider>
                                                        </RegistrationProvider>
                                                    ),
                                                },
                                                {
                                                    path: 'history',
                                                    element: <ProtectedStoreHistoryPage />,
                                                },
                                                {
                                                    path: 'layout',
                                                    element: (
                                                        <LayoutProvider>
                                                            <ProtectedStoreLayoutPageLayout />
                                                        </LayoutProvider>
                                                    ),
                                                    children: [{ path: '', element: <ProtectedStoreLayoutPage /> }],
                                                },
                                                {
                                                    element: <ProtectedAdminRoute requiredLevel={2} />,
                                                    children: [
                                                        {
                                                            path: 'settings',
                                                            element: <StoreSettingsPage />,
                                                        },
                                                    ],
                                                },
                                            ],
                                        },
                                        {
                                            path: 'devices',
                                            element: <ProtectedDevicesPage />,
                                        },
                                        {
                                            element: <ProtectedAdminRoute requiredLevel={2} />,
                                            children: [
                                                {
                                                    path: 'admin',
                                                    element: <AdminPageLayout />,
                                                    children: [
                                                        {
                                                            path: '',
                                                            element: <Navigate to="store" replace />,
                                                        },
                                                        {
                                                            path: 'store',
                                                            element: <AdminStorePage />,
                                                        },
                                                        {
                                                            path: 'layouts',
                                                            element: <LayoutsPageLayout />,
                                                            children: [
                                                                { path: '', element: <LayoutsPage /> },
                                                                { path: 'new', element: <LayoutPackagePage /> },
                                                                { path: ':id', element: <LayoutPackagePage /> },
                                                            ],
                                                        },
                                                        {
                                                            path: 'store/new',
                                                            element: <AdminStorePageDialog />,
                                                        },
                                                        {
                                                            path: 'store/:id',
                                                            element: <AdminStorePageDialog />,
                                                        },
                                                        {
                                                            path: 'role',
                                                            element: <AdminRolePage />,
                                                        },
                                                        {
                                                            path: 'role/new',
                                                            element: <AdminRolePageDialog />,
                                                        },
                                                        {
                                                            path: 'role/:id',
                                                            element: <AdminRolePageDialog />,
                                                        },
                                                        {
                                                            path: 'account',
                                                            element: <AdminAccountPage />,
                                                        },
                                                        {
                                                            path: 'account/new',
                                                            element: <AdminAccountPageDialog />,
                                                        },
                                                        {
                                                            path: 'account/:id',
                                                            element: <AdminAccountPageDialog />,
                                                        },
                                                        {
                                                            path: 'cluster',
                                                            element: <AdminClusterPage />,
                                                        },
                                                        {
                                                            path: 'settings',
                                                            element: <AdminSettingPage />,
                                                        },
                                                        // {
                                                        //     element: <ProtectedAdminRoute requiredLevel={3} />,
                                                        //     children: [
                                                        //         {
                                                        //             path: 'corporation',
                                                        //             element: <AdminCorporationPage />,
                                                        //         },
                                                        //     ],
                                                        // },
                                                    ],
                                                },
                                            ],
                                        },
                                        {
                                            path: 'credits',
                                            element: <CreditsPage />,
                                        },
                                        {
                                            path: 'changelog',
                                            element: <ChangelogPage />,
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                {
                    path: '*',
                    element: <DefaultRedirect />,
                },
            ],
        },
    ],
    {
        future: {
            v7_fetcherPersist: true,
            v7_normalizeFormMethod: true,
            v7_partialHydration: true,
            v7_relativeSplatPath: true,
            v7_skipActionErrorRevalidation: true,
        },
    },
);

export default router;
